import type { TokenSummaryDto } from '@rhcst/shared';

/**
 * Contract-safety chips (Phase 11, Slice 11G sub-step 4; console redesign
 * v2). Sentence case, not uppercase — deliberately distinct from every
 * verdict/lifecycle/status chip elsewhere, which are uppercase (confirmed
 * against the design reference, not a mockup slip).
 *
 * Three independent facts, each shown honestly rather than only when it is
 * good news:
 *
 * - Ownership renounce has three real states — renounced, not renounced,
 *   and never checked/undeterminable (`ownership_renounced: null` covers
 *   both "never checked" and "checked, but owner() reverted" — see
 *   `ownership-check.ts`'s own header for why those two collapse into one
 *   value rather than being distinguished further). "Not renounced" is
 *   shown neutrally, not as an alarm: most legitimate tokens keep an owner,
 *   and `contractSafetyScore` itself only discounts this modestly (70/100),
 *   never floors it.
 * - "Not blocklisted" only renders when the token genuinely is not
 *   blocklisted — a blocklisted token already gets its own alarm-toned
 *   chip elsewhere (`PairRow`/`PairDetail`'s existing "Blocklisted" chip),
 *   and showing both here would be redundant at best, contradictory at
 *   worst.
 * - "Liquidity lock: unknown" is always present, unconditionally. No
 *   verified locker registry exists for this chain yet, and that gap is
 *   itself worth showing rather than implying safety by omission — the
 *   same honest-absence convention as `.unscored`.
 */

export interface SafetyChipsProps {
  token: TokenSummaryDto;
  /** Compact: at most one chip, only when it is a positive finding worth a
   *  glance (the featured card). Full: all three facts (Pair Detail's
   *  Tokens panel). */
  variant: 'compact' | 'full';
}

export function SafetyChips({ token, variant }: SafetyChipsProps) {
  if (variant === 'compact') {
    if (token.ownership_renounced !== true) return null;
    return (
      <span className="safety-chip" data-tone="good" title="owner() resolves to the zero or burn address">
        Ownership renounced
      </span>
    );
  }

  return (
    <span className="row" style={{ gap: '6px', flexWrap: 'wrap' }}>
      {token.ownership_renounced === true && (
        <span className="safety-chip" data-tone="good" title="owner() resolves to the zero or burn address">
          Ownership renounced
          {token.contract_safety_tags.length > 0 ? ` (${token.contract_safety_tags.join(', ')})` : ''}
        </span>
      )}
      {token.ownership_renounced === false && (
        <span
          className="safety-chip"
          data-tone="neutral"
          title="owner() resolves to a real address — the deployer retains owner-level control. Not necessarily unsafe on its own."
        >
          Ownership not renounced
        </span>
      )}
      {token.ownership_renounced === null && (
        <span
          className="safety-chip"
          data-tone="unknown"
          title="No ownership-renounce check has run for this token yet, or owner() reverted — never read as 'not renounced'"
        >
          Ownership: not checked
        </span>
      )}

      {!token.is_blocklisted && (
        <span className="safety-chip" data-tone="neutral">
          Not blocklisted
        </span>
      )}

      <span
        className="safety-chip"
        data-tone="unknown"
        title="No verified liquidity-locker registry exists for this chain yet"
      >
        Liquidity lock: unknown
      </span>
    </span>
  );
}
